<template>
  <div>
    <!-- Sample Selector -->
    <ol
      class="flex items-center w-full p-3 justify-around space-x-2 text-sm font-medium text-center text-gray-500 bg-white border border-gray-200 rounded-lg shadow-sm sm:text-base"
    >
      <li
        v-for="(request, index) in filteredRequests"
        :key="request.id"
        class="flex items-center cursor-pointer"
        :class="{ 'text-custom-blue': selectedSample === request.id }"
        @click="selectSample(request.id)"
      >
        <span
          class="flex items-center justify-center w-5 h-5 me-2 text-xs border rounded-full shrink-0"
          :class="selectedSample === request.id ? 'border-custom-blue' : 'border-gray-500'"
        >
          {{ index + 1 }}
        </span>
        {{ request.test_type.name }}
      </li>
    </ol>
    <div v-if="filteredRequests.length === 0" class="text-center text-green-600 font-medium mt-6">
      All Requested Lab test samples have been collected!
    </div>

    <!-- Sample Collection Form (for new samples) -->
    <div class="grid grid-cols-1 gap-4 h-[56vh] overflow-y-auto mt-3">
      <div v-if="selectedSample && !isCollectedOrFinal(selectedTest)">
        <div class="grid grid-cols-2 gap-6 mt-4 px-8">
          <div class="mb-4">
            <label class="font-medium">Sample Type:</label><br />
            {{ labForms[selectedSample]?.sample_type }}
          </div>

          <div class="flex flex-col">
            <label class="font-medium">Sample Status:</label>
            <select
              v-model="labForms[selectedSample].status"
              class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-custom-yellow focus:ring focus:ring-custom-yellow focus:ring-opacity-50 bg-gray-50"
            >
              <option v-for="status in statuses" :key="status.id" :value="status">
                {{ status.label }}
              </option>
            </select>
          </div>

          <div class="mb-4 col-span-2">
            <label class="font-medium">Sample Condition:</label>
            <textarea
              v-model="labForms[selectedSample].condition"
              rows="4"
              placeholder="Type..."
              class="w-full border-gray-300 rounded p-2 bg-gray-50 mt-2"
            />
          </div>
        </div>

        <!-- Submit New Sample -->
        <div class="mt-4 px-8 flex justify-end">
          <button
            @click="handleSampleSubmit"
            class="px-4 py-2 bg-custom-yellow text-white rounded hover:bg-yellow-600"
          >
            {{ showLoader ? 'Submitting...' : 'Submit Sample' }}
          </button>
        </div>
      </div>

      <!-- Submitted Samples Table -->
      <div class="mt-8 px-8">
        <h2 class="text-xl font-bold mb-4">Submitted Samples</h2>
        <table class="min-w-full divide-y divide-gray-200">
          <thead class="bg-gray-50">
            <tr>
              <th class="px-6 py-3 text-left font-medium text-gray-600">Test</th>
              <th class="px-6 py-3 text-left font-medium text-gray-600">Sample Type</th>
              <th class="px-6 py-3 text-left font-medium text-gray-600">Sample ID</th>
              <th class="px-6 py-3 text-left font-medium text-gray-600">Sample Status</th>
              <th class="px-6 py-3 text-left font-medium text-gray-600">Collected By</th>
              <th class="px-6 py-3 text-left font-medium text-gray-600">Sample Condition</th>
              <th class="px-6 py-3 text-left font-medium text-gray-600"></th>
            </tr>
          </thead>
          <tbody class="bg-white divide-y divide-gray-200">
            <tr v-for="sample in submittedSamples" :key="sample.id">
              <td class="px-6 py-4 whitespace-nowrap">{{ sample.test_type?.name }}</td>
              <td class="px-6 py-4 whitespace-nowrap">{{ sample.sample_type_override || sample.test_type?.sample?.name }}</td>
              <td class="px-6 py-4 whitespace-nowrap">{{ sample.sample_code }}</td>
              <td
                class="px-6 py-4 whitespace-nowrap"
              >
                <span
                  :class="[
                    sample.status?.name === 'Published'
                      ? 'px-2 py-1 text-xs font-semibold bg-green-500 text-white rounded'
                      : ''
                  ]"
                >
                  {{ sample.status?.name }}
                </span>
              </td>
              <td class="px-6 py-4 whitespace-nowrap">{{ sample.collecter?.Username }}</td>
              <td class="px-6 py-4 whitespace-nowrap">{{ sample.sample_condition }}</td>
              <td class="px-6 py-4 whitespace-nowrap">
                <button
                  v-if="canEditSample(sample)"
                  @click="openEditModal(sample)"
                  class="px-2 py-1 bg-custom-yellow text-white rounded hover:bg-yellow-600 text-xs"
                >
                  Edit
                </button>
                <span v-else class="text-gray-400 text-xs">—</span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- Edit Sample Modal -->
    <EditLabSample
      :show="editModal.show"
      :sample="editModal.sample"
      @close="editModal.show = false"
      @updated="handleSampleUpdated"
    />
  </div>
</template>

<script>
import apiClient from '@/services/api';
import EditLabSample from '@/views/modals/Laboratory/EditLabSample.vue';

export default {
  name: 'SampleCollectionTab',
  components: { EditLabSample },
  props: { requests: { type: Array, required: true } },

  data() {
    return {
      selectedSample: null,
      labForms: {},
      showLoader: false,
      statuses: [
        { id: 1, label: 'Collected' },
        { id: 2, label: 'Recollect' },
      ],
      editModal: { show: false, sample: null },
    };
  },

  computed: {
    // FIXED: Show all non-final tests in banner (not just ST-006)
    filteredRequests() {
      const filtered = this.requests.filter(item =>
        !['ST-003', 'ST-013'].includes(item.status?.code) // exclude only Completed & Published
      );

      // Auto-select first if none selected
      if (filtered.length > 0 && !this.selectedSample) {
        this.$nextTick(() => this.selectSample(filtered[0].id));
      }

      return filtered;
    },

    selectedTest() {
      return this.requests.find(r => r.id === this.selectedSample) || {};
    },

    submittedSamples() {
      return this.requests.filter(item =>
        ['ST-011', 'ST-003', 'ST-013', 'ST-012'].includes(item.status?.code)
      );
    },
  },

  watch: {
    requests: {
      handler(newVal) {
        // Re-select first pending if nothing selected
        if (newVal?.length && !this.selectedSample) {
          const pending = newVal.filter(r => !this.isCollectedOrFinal(r));
          if (pending.length > 0) {
            this.selectSample(pending[0].id);
          }
        }
      },
      immediate: true,
    },
  },

  methods: {
    selectSample(requestId) {
      const test = this.requests.find(r => r.id === requestId);
      if (!test) return;

      this.selectedSample = requestId;

      // Initialize form
      if (!this.labForms[requestId]) {
        this.labForms[requestId] = {
          sample_type: test.test_type?.sample?.name || '',
          condition: '',
          status: '',
        };
      }
    },

    async handleSampleSubmit() {
      if (!this.labForms[this.selectedSample]?.status) {
        this.$toast.error('Please select a status');
        return;
      }

      this.showLoader = true;
      try {
        const payload = {
          samples: [{
            labRequestId: this.selectedSample,
            sample_type: this.labForms[this.selectedSample].sample_type,
            condition: this.labForms[this.selectedSample].condition,
            status: this.labForms[this.selectedSample].status,
          }],
        };

        const response = await apiClient.collectLabSample(payload);
        if (response.success) {
          this.$toast.success('Sample collected successfully');
          this.$emit('refetch-requests');

          // Wait for fresh data
          await this.$nextTick();

          // Find next test that needs collection
          const nextPending = this.requests.find(r =>
            !this.isCollectedOrFinal(r) &&
            r.status?.code !== 'ST-009' &&
            r.id !== this.selectedSample // avoid re-selecting current
          );

          if (nextPending) {
            this.selectSample(nextPending.id);
          } else {
            this.selectedSample = null;
          }

          // Reset form for new selection
          if (this.selectedSample) {
            const current = this.requests.find(r => r.id === this.selectedSample);
            this.labForms[this.selectedSample] = {
              sample_type: current.test_type?.sample?.name || '',
              condition: '',
              status: '',
            };
          }
        }
      } catch (err) {
        this.$toast.error('Network error');
        console.error(err);
      } finally {
        this.showLoader = false;
      }
    },

    openEditModal(sample) {
      this.editModal = { show: true, sample: { ...sample } };
    },

    handleSampleUpdated() {
      this.editModal.show = false;
      this.$emit('refetch-requests');
      this.$toast.success('Sample updated');
    },

    canEditSample(sample) {
      return sample.status?.code === 'ST-011';
    },

    isCollectedOrFinal(test) {
      return ['ST-003', 'ST-013', 'ST-011'].includes(test.status?.code);
    },
  },
};
</script>

<style lang="css" scoped>
/* keep your existing styles */
</style>